import { MultiBrandSupportUserReportConversationReasonType } from '@zg-rentals/ts-satellite-client';
import {
  type ClickstreamEventParams,
  SEMANTIC_EVENT_NAME,
  TOPIC_TAG_TXT,
  TRIGGER_OBJECT,
  TRIGGER_TYPE,
} from './globals';
import { trackRenterProfileListingEvent } from '../../analytics';
import { getZrmListingInfo, getZrmRentalsLead, getZrmUserInfo } from '@zg-rentals/clickstream';

const REPORT_PROBLEM_MODAL = 'report_problem_modal';

// https://dataportal.zillowgroup.net/event-registry/6375
const emitReportConversationAsSpamEvent = ({ user, listing, triggerLocation, lead }: ClickstreamEventParams) =>
  trackRenterProfileListingEvent({
    envelope: {
      event_type_id: '6375',
      event_type_version_id: '3',
      event_template_id: '629',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.Interaction,
      trigger_location_nm: triggerLocation,
      trigger_source_nm: 'spam_radio_button',
      trigger_object_nm: REPORT_PROBLEM_MODAL,
    },
    semantic: {
      semantic_event_nm: SEMANTIC_EVENT_NAME.ReportConversation,
      topic_tag_txt: [TOPIC_TAG_TXT.ZrmSatellite],
    },
    renter_profile: null,
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
    ...getZrmRentalsLead(lead),
  });

// https://dataportal.zillowgroup.net/event-registry/6376
const emitReportConversationAsDiscriminatoryEvent = ({
  user,
  listing,
  triggerLocation,
  lead,
}: ClickstreamEventParams) =>
  trackRenterProfileListingEvent({
    envelope: {
      event_type_id: '6376',
      event_type_version_id: '3',
      event_template_id: '629',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.Interaction,
      trigger_location_nm: triggerLocation,
      trigger_source_nm: 'discriminatory_radio_button',
      trigger_object_nm: REPORT_PROBLEM_MODAL,
    },
    semantic: {
      semantic_event_nm: SEMANTIC_EVENT_NAME.ReportConversation,
      topic_tag_txt: [TOPIC_TAG_TXT.ZrmSatellite],
    },
    renter_profile: null,
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
    ...getZrmRentalsLead(lead),
  });

// https://dataportal.zillowgroup.net/event-registry/6377
const emitReportConversationAsOtherEvent = ({ user, listing, triggerLocation, lead }: ClickstreamEventParams) =>
  trackRenterProfileListingEvent({
    envelope: {
      event_type_id: '6377',
      event_type_version_id: '3',
      event_template_id: '629',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.Interaction,
      trigger_location_nm: triggerLocation,
      trigger_source_nm: 'other_radio_button',
      trigger_object_nm: REPORT_PROBLEM_MODAL,
    },
    semantic: {
      semantic_event_nm: SEMANTIC_EVENT_NAME.ReportConversation,
      topic_tag_txt: [TOPIC_TAG_TXT.ZrmSatellite],
    },
    renter_profile: null,
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
    ...getZrmRentalsLead(lead),
  });

// https://dataportal.zillowgroup.net/event-registry/6378
export const OPEN_REPORT_PROBLEM_MODAL_EVENT_TYPE_ID = '6378';
export const emitOpenReportProblemModalEvent = ({ user, listing, triggerLocation, lead }: ClickstreamEventParams) =>
  trackRenterProfileListingEvent({
    envelope: {
      event_type_id: OPEN_REPORT_PROBLEM_MODAL_EVENT_TYPE_ID,
      event_type_version_id: '3',
      event_template_id: '629',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.Impression,
      trigger_location_nm: triggerLocation,
      trigger_source_nm: 'report_conversation_modal',
      trigger_object_nm: TRIGGER_OBJECT.NoTriggerObject,
    },
    semantic: {
      semantic_event_nm: SEMANTIC_EVENT_NAME.ViewContent,
      topic_tag_txt: [TOPIC_TAG_TXT.ZrmSatellite],
    },
    renter_profile: null,
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
    ...getZrmRentalsLead(lead),
  });

export const reportReasonClickstreamEventMap = {
  [MultiBrandSupportUserReportConversationReasonType.SPAM]: emitReportConversationAsSpamEvent,
  [MultiBrandSupportUserReportConversationReasonType.DISCRIMINATORY_OR_ABUSIVE]:
    emitReportConversationAsDiscriminatoryEvent,
  [MultiBrandSupportUserReportConversationReasonType.OTHER]: emitReportConversationAsOtherEvent,
} as const;
